import type { FormEquivalence, FormSettings, FormSynonym } from './api.ts'

/**
 * What the forms screens show and change, worked out before they draw (#607).
 *
 * Pure, so the rules -- one rule per pair, never a form onto itself, what a
 * form's row counts -- are tested here as well as enforced by the server.
 */

const pair = (one: FormEquivalence) => `${one.prescribed}>${one.keyed}`

/** The list with this rule in it, replacing an earlier rule for the same pair. */
export function withEquivalence(
  list: readonly FormEquivalence[],
  added: FormEquivalence,
): FormEquivalence[] {
  if (added.prescribed === added.keyed) return [...list]
  return [...list.filter((one) => pair(one) !== pair(added)), added]
}

export function withoutEquivalence(
  list: readonly FormEquivalence[],
  removed: FormEquivalence,
): FormEquivalence[] {
  return list.filter((one) => pair(one) !== pair(removed))
}

/** Whether the list already has a rule for this pair, under any condition. */
export function hasEquivalence(list: readonly FormEquivalence[], candidate: FormEquivalence) {
  return list.some((one) => pair(one) === pair(candidate))
}

/** An account's taught names of one form, waiting first: that is the work. */
export function synonymsOf(synonyms: readonly FormSynonym[], form: string): FormSynonym[] {
  const order = { pending: 0, approved: 1, rejected: 2 } as const
  return synonyms
    .filter((one) => one.form === form)
    .sort((a, b) => order[a.status] - order[b.status] || a.name.localeCompare(b.name))
}

/** One row of the forms list. */
export interface FormRow {
  readonly id: string
  readonly label: string
  readonly builtInNames: readonly string[]
  readonly approved: number
  readonly pending: number
}

/** Every form with what the account has taught it, the forms waiting on somebody first. */
export function formRows(settings: FormSettings): FormRow[] {
  return settings.forms
    .map((form) => {
      const taught = settings.synonyms.filter((one) => one.form === form.id)
      return {
        id: form.id,
        label: form.label,
        builtInNames: form.builtInNames,
        approved: taught.filter((one) => one.status === 'approved').length,
        pending: taught.filter((one) => one.status === 'pending').length,
      }
    })
    .sort((a, b) => Number(b.pending > 0) - Number(a.pending > 0))
}
